// ./RegretRoulette.js
import React, { useState, useEffect, useRef } from "react";
import DareInfoModal from "./ui/DareInfoModal";
import { Howl } from "howler";
import { getContract, parseUnits, parseEther, erc20Abi, decodeEventLog } from 'viem';
import { useWalletClient, useAccount, usePublicClient, useChainId, useSwitchChain } from 'wagmi';
import { regretRouletteABI } from './abis/regretRouletteABI';
import Confetti from "react-confetti";
import RegretWheel from "./ui/RegretWheel";
import RedemptionWheel from "./ui/RedemptionWheel";
import DareWheel from "./ui/DareWheel";
import WarningModal from "./ui/WarningModal";
import HowItWorksModal from "./ui/HowItWorksModal";
import ShareButton from "./ui/ShareButton";
import BuyButton from "./ui/BuyButton";
import RedemptionInfoModal from "./ui/RedemptionInfoModal";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { useMediaQuery } from 'react-responsive';
import { FiMenu } from 'react-icons/fi';
import LiveFeed from "./ui/LiveFeed";
import { useDisconnect } from 'wagmi';
import { useWeb3Modal } from '@web3modal/wagmi/react';
import { Navigation } from "./components/Navigation";
import { createPublicClient, http } from 'viem';

import spinSoundFile from "./assets/sounds/spin.mp3";
import winSoundFile from "./assets/sounds/win.mp3";
import jackpotSoundFile from "./assets/sounds/jackpot.mp3";
import losingSoundFile from "./assets/sounds/losing.mp3";

const spinSound = new Howl({ src: [spinSoundFile] });
const winSound = new Howl({ src: [winSoundFile] });
const jackpotSound = new Howl({ src: [jackpotSoundFile] });
const losingSound = new Howl({ src: [losingSoundFile] });

const prizes = [
  { label: "10k $REGRET", value: 10000, sound: winSound, prizeType: 0 },
  { label: "50k $REGRET", value: 50000, sound: winSound, prizeType: 0 },
  { label: "100k $REGRET", value: 100000, sound: winSound, prizeType: 0 },
  { label: "250k $REGRET", value: 250000, sound: winSound, prizeType: 0 },
  { label: "500k $REGRET", value: 500000, sound: winSound, prizeType: 0 },
  { label: "Free Spin 🎉", value: 0, sound: winSound, prizeType: 1 },
  { label: "Double Prize 🔁", value: 0, sound: winSound, prizeType: 2 },
  { label: "You Lose ❌", value: 0, sound: losingSound, prizeType: 3 },
  { label: "Mystery 🪄", value: -1, sound: winSound, prizeType: 4 },
  { label: "Regret Mirror 🪞", value: 100000, sound: winSound, prizeType: 5 },
  { label: "Tax the wallet 💸", value: 0, sound: losingSound, prizeType: 6 },
  { label: "JACKPOT 💎", value: 1000000, sound: jackpotSound, prizeType: 7 },
];

const probabilities = [20, 15, 10, 8, 5, 10, 7, 20, 7, 3, 1, 0.4];

// Contract addresses
const regretTokenAddress = '0x18A05C5d0B5787aaBE64d209997fbFfa3Df77283'; // $REGRET token
const regretRouletteAddress = '0x180535bbc5E4f83afE6bE1ad041177Da5aC63Ee9'; // RegretRoulette contract
const SPIN_COST = parseUnits('100000', 18); // 100k $REGRET

// Metis Andromeda mainnet configuration
const metisChain = {
  id: 1088,
  name: 'Metis Andromeda',
  network: 'metis',
  nativeCurrency: {
    decimals: 18,
    name: 'Metis',
    symbol: 'METIS',
  },
  rpcUrls: {
    default: { http: ['https://andromeda.metis.io/?owner=1088'] },
    public: { http: ['https://andromeda.metis.io/?owner=1088'] },
  },
  blockExplorers: {
    default: { name: 'Metis Explorer', url: 'https://explorer.metis.io' },
  },
};

// Create a custom public client for Metis
const createMetisClient = () => {
  return createPublicClient({
    chain: metisChain,
    transport: http('https://andromeda.metis.io/?owner=1088', {
      batch: {
        multicall: true
      }
    })
  });
};

// Add this function before the RegretRoulette component
const readRegretContract = async (publicClient, address) => {
  try {
    console.log('Reading REGRET token contract...');
    const tokenContract = getContract({
      address: regretTokenAddress,
      abi: erc20Abi,
      client: publicClient
    });

    // Read basic token info
    const name = await tokenContract.read.name();
    const symbol = await tokenContract.read.symbol();
    const decimals = await tokenContract.read.decimals();
    const totalSupply = await tokenContract.read.totalSupply();
    const balance = await tokenContract.read.balanceOf([address]);

    console.log('Token Contract Details:');
    console.log('Name:', name);
    console.log('Symbol:', symbol);
    console.log('Decimals:', decimals);
    console.log('Total Supply:', totalSupply.toString());
    console.log('Your Balance:', balance.toString());
    console.log('Your Balance in REGRET:', Number(balance) / 1e18);

    return {
      name,
      symbol,
      decimals,
      totalSupply,
      balance
    };
  } catch (error) {
    console.error('Error reading REGRET contract:', error);
    throw error;
  }
};

export default function RegretRoulette() {
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [resultIndex, setResultIndex] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [shake, setShake] = useState(false);
  const [jackpotHit, setJackpotHit] = useState(false);
  const [doublePrizeActive, setDoublePrizeActive] = useState(false);
  const [pendingDoublePrize, setPendingDoublePrize] = useState(false);
  const [freeSpinActive, setFreeSpinActive] = useState(false);
  const [consecutiveLosses, setConsecutiveLosses] = useState(0);
  const [showRedemptionSpin, setShowRedemptionSpin] = useState(false);
  const [redemptionResult, setRedemptionResult] = useState(null);
  const [showRedemptionPopup, setShowRedemptionPopup] = useState(false);
  const [mysteryAmount, setMysteryAmount] = useState(null);
  const [finalPayout, setFinalPayout] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showRedemptionInfo, setShowRedemptionInfo] = useState(false);
  const [showDareWheel, setShowDareWheel] = useState(false);
  const [dareEligible, setDareEligible] = useState(false);
  const [showDareInfo, setShowDareInfo] = useState(false);
  const [showDarePopup, setShowDarePopup] = useState(false);
  const [showDareOption, setShowDareOption] = useState(false);
  const [dareResult, setDareResult] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useMediaQuery({ query: '(max-width: 640px)' });
  const [liveSpins, setLiveSpins] = useState([]);
  const spinHandledRef = useRef(false);

  const { data: walletClient } = useWalletClient();
  const { isConnected, address } = useAccount();
  const { open } = useWeb3Modal();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();
  const publicClient = createMetisClient();
  
  // Check if we're on the correct network
  useEffect(() => {
    if (isConnected && chainId !== metisChain.id) {
      alert('Please switch to Metis Andromeda network');
      switchChain?.({ chainId: metisChain.id });
    }
  }, [isConnected, chainId, switchChain]);

  // Add this useEffect to read contract when connected
  useEffect(() => {
    if (isConnected && address) {
      readRegretContract(publicClient, address)
        .then(contractInfo => {
          console.log('Contract info loaded successfully:', contractInfo);
        })
        .catch(error => {
          console.error('Failed to load contract info:', error);
        });
    }
  }, [isConnected, address, publicClient]);

  const handleConnect = async () => {
    try {
      await open();
    } catch (err) {
      console.error('Failed to open Web3Modal:', err);
    }
  };

// Show warning modal once wallet is connected
useEffect(() => {
  if (isConnected) {
    setShowWarning(true);
  }
}, [isConnected]);

const handleSpin = async () => {
  if (!walletClient || !address) {
    alert('Wallet not connected');
    return;
  }

  // Check if we're on the correct network
  if (chainId !== metisChain.id) {
    try {
      await switchChain?.({ chainId: metisChain.id });
      await new Promise(resolve => setTimeout(resolve, 1000));
    } catch (error) {
      alert('Failed to switch to Metis network. Please switch manually in MetaMask.');
      return;
    }
  }

  try {
    const client = walletClient;
    const metisBalance = await publicClient.getBalance({ address });
    const minGas = parseEther('0.01');
    if (metisBalance < minGas) {
      alert('⚠️ Not enough METIS for gas fees');
      return;
    }

    // Check $REGRET allowance
    const tokenContract = getContract({
      address: regretTokenAddress,
      abi: erc20Abi,
      client
    });
    const allowance = await tokenContract.read.allowance([address, regretRouletteAddress]);
    if (allowance < SPIN_COST) {
      if (!window.confirm('You need to approve the game to spend your $REGRET tokens. Approve now?')) return;
      try {
        const approveHash = await tokenContract.write.approve([
          regretRouletteAddress,
          parseUnits('1000000000', 18)
        ]);
        await publicClient.waitForTransactionReceipt({ hash: approveHash });
        alert('Approval successful! Now you can spin.');
      } catch (approveError) {
        alert('Approval failed: ' + (approveError?.message || String(approveError)));
      }
      return;
    }

    setShowPopup(true);
    setResult({ label: 'Waiting for transaction confirmation...', value: 0 });

    const rouletteContract = getContract({
      address: regretRouletteAddress,
      abi: regretRouletteABI,
      client
    });

    let spinHash;
    try {
      spinHash = await rouletteContract.write.spin({
        gas: 500000n,
        account: address
      });
    } catch (initialError) {
      if (initialError.message.includes('mobile') || initialError.message.includes('MetaMask')) {
        spinHash = await rouletteContract.write.spin({
          gas: 1500000n,
          account: address
        });
      } else {
        setShowPopup(false);
        throw initialError;
      }
    }

    // Wait for transaction receipt
    const receipt = await publicClient.waitForTransactionReceipt({ hash: spinHash });
    console.log('📄 Full Transaction Receipt:', JSON.stringify(receipt, (key, value) => 
      typeof value === 'bigint' ? value.toString() : value
    ));

    // Parse Spin event from logs to determine actual prize
    let actualIndex = null;
    let actualPrize = null;
    let actualAmount = null;

    try {
      const spinEvent = receipt.logs.find(log => 
        log.address.toLowerCase() === regretRouletteAddress.toLowerCase() &&
        log.topics[0] === '0xbb8510115ee21ef8b895a1d5115a4c06dfd8b33beeedc8f261c99e5ca5082d29'
      );

      if (spinEvent) {
        const decodedSpin = decodeEventLog({
          abi: regretRouletteABI,
          data: spinEvent.data,
          topics: spinEvent.topics,
        });
        
        const contractPrizeType = Number(decodedSpin.args.prizeType);
        const amountWon = BigInt(decodedSpin.args.amountWon);
        
        console.log('Contract Prize Type:', contractPrizeType);
        console.log('Contract Amount Won:', Number(amountWon) / 1e18);
        
        // Find the matching prize based on contract result
        actualIndex = prizes.findIndex(p => {
          if (p.prizeType !== contractPrizeType) return false;
          if (contractPrizeType === 0 || contractPrizeType === 7) {
            return parseUnits(p.value.toString(), 18) === amountWon;
          }
          return true;
        });
        
        if (actualIndex === -1) {
          console.warn('No matching prize found for contract result, using first prize');
          actualIndex = 0;
        }
        
        actualPrize = prizes[actualIndex];
        actualAmount = amountWon;
        
        console.log('Selected Prize:', actualPrize);
        console.log('Selected Amount:', Number(actualAmount) / 1e18);
      }
    } catch (e) {
      console.error('Error parsing spin event:', e);
      actualIndex = null;
      actualPrize = null;
      actualAmount = null;
    }

    // Now that we know the actual prize, start the animation
    if (actualIndex !== null) {
      setResultIndex(actualIndex);
      setResult({
        ...actualPrize,
        value: actualPrize.value,
        contractAmount: actualAmount
      });
      setSpinning(true);
      setShowPopup(false);
      
      // Play spin sound
      spinSound.stop();
      spinSound.play();

      // Add to last spins history
      setLiveSpins(prev => [
        {
          wallet: address,
          result: actualPrize.label,
          amount: actualAmount ? (Number(actualAmount) / 1e18) : undefined
        },
        ...prev.slice(0, 9),
      ]);

      // Show result after animation
      setTimeout(() => {
        setSpinning(false);
        setShowPopup(true);
        
        // Play appropriate sound
        if (actualPrize.label === "JACKPOT 💎") {
          jackpotSound.stop();
          jackpotSound.play();
        } else {
          actualPrize?.sound?.play();
        }

        // Handle special prizes
        if (actualPrize.label === "Double Prize 🔁") {
          setDoublePrizeActive(true);
          setPendingDoublePrize(true);
        }

        if (actualPrize.label === "Free Spin 🎉") {
          setFreeSpinActive(true);
        }

        if (["You Lose ❌", "Tax the wallet 💸"].includes(actualPrize.label)) {
          setConsecutiveLosses(prev => {
            const newLosses = prev + 1;
            if (newLosses >= 2) {
              setTimeout(() => setShowRedemptionSpin(true), 800);
              return 0;
            }
            return newLosses;
          });
          setShake(true);
          setTimeout(() => setShake(false), 1000);
        } else {
          setConsecutiveLosses(0);
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 3000);
        }

        if (actualPrize.label === "JACKPOT 💎") {
          setJackpotHit(true);
          setTimeout(() => setJackpotHit(false), 6000);
        }
      }, 3000); // Match this with your wheel animation duration
    } else {
      setSpinning(false);
      setShowPopup(false);
    }

  } catch (err) {
    setSpinning(false);
    setShowPopup(false);
    alert('Spin failed: ' + (err?.message || String(err)));
  }
};

const handleEndSpin = (index) => {
  if (spinHandledRef.current) return;
  spinHandledRef.current = true;

  setSpinning(false);
  const prize = prizes[index];
  setResult(prize);

  // Sound
  if (prize.label === "JACKPOT 💎") {
    jackpotSound.stop();
    jackpotSound.play();
  } else {
    prize?.sound?.play();
  }

  // Mystery logic
  let payout = prize.value;
  if (prize.label === "Mystery 🪄") {
    const mysteryOptions = [10000, 25000, 50000, 75000, 100000, 250000];
    payout = mysteryOptions[Math.floor(Math.random() * mysteryOptions.length)];
    setMysteryAmount(payout);
  }

  // Double Prize logic
  let actualPayout = payout;
  const isLoss = ["You Lose ❌", "Tax the wallet 💸"].includes(prize.label);

  if (doublePrizeActive && payout > 0) {
    actualPayout *= 2;
    setDoublePrizeActive(false);
    setPendingDoublePrize(false);
  } else if (pendingDoublePrize && isLoss) {
    setDoublePrizeActive(false);
    setPendingDoublePrize(false);
  }

  setFinalPayout(actualPayout);

  const isEligibleForDare = actualPayout >= 250000;
  setShowDareOption(isEligibleForDare);
  setDareEligible(isEligibleForDare);

  if (prize.label === "Double Prize 🔁") {
    setDoublePrizeActive(true);
    setPendingDoublePrize(true);
  }

  if (prize.label === "Free Spin 🎉") setFreeSpinActive(true);

  // Prize payout is now handled by the contract after a successful spin.
  // No need to call sendPrizeToWallet for normal spin results.

  if (isLoss) {
    setConsecutiveLosses((prev) => {
      const newLosses = prev + 1;
      if (newLosses >= 2) {
        setTimeout(() => setShowRedemptionSpin(true), 800);
        return 0;
      }
      return newLosses;
    });
    setShake(true);
    setTimeout(() => setShake(false), 1000);
  } else {
    setConsecutiveLosses(0);
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 3000);
  }

  if (prize.label === "JACKPOT 💎") {
    setJackpotHit(true);
    setTimeout(() => setJackpotHit(false), 6000);
  }

  // ⛔ Feed wordt hier **NIET** meer geüpdatet.
  setShowPopup(true);

  setTimeout(() => {
    spinHandledRef.current = false;
  }, 1000);
};

const sendPrizeToWallet = async (amount) => {
  if (!walletClient || !address) {
    console.warn("❌ No wallet client available.");
    return;
  }

  try {
    const client = walletClient;

    const balance = await publicClient.getBalance({ address });
    const minRequired = parseEther("0.01");

    if (balance < minRequired) {
      alert("⚠️ Not enough METIS to cover gas fees. Please top up your wallet.");
      return;
    }

    const contract = getContract({
      address: regretRouletteAddress,
      abi: regretRouletteABI,
      client
    });

    const hash = await contract.write.distributePrize([
      address,
      parseUnits(amount.toString(), 18)
    ]);

    console.log("✅ Transaction hash:", hash);

    const receipt = await client.waitForTransactionReceipt({ hash });

    if (receipt.status === "success") {
      console.log("🎉 Prize successfully distributed!", receipt);
    } else {
      console.warn("⚠️ Transaction reverted:", receipt);
      alert("⚠️ Transaction reverted. Possibly failed in the contract logic.");
    }

  } catch (err) {
    console.error("❌ Error sending prize:", err);
    let errorMsg = "❌ Transaction failed. Check your balance or try again later.";
    if (err?.message) {
      errorMsg += "\n\nDetails: " + err.message;
    } else if (err?.data?.message) {
      errorMsg += "\n\nDetails: " + err.data.message;
    }
    alert(errorMsg);
  }
};

  return (
    <>
      <Navigation />
      <div className={`relative flex flex-col items-center justify-center min-h-screen text-white bg-black ${shake ? "animate-shake" : ""}`}>
        {showConfetti && <Confetti />}
        {jackpotHit && (
          <div className="fixed top-0 w-full bg-yellow-400 text-black text-center font-extrabold py-2 z-50 animate-pulse text-lg">
            💎 JACKPOT HIT! 1.000.000 $REGRET 💎
          </div>
        )}

      <div className="absolute top-4 right-4 z-20 hidden sm:flex flex-col items-end space-y-2 sm:right-4 right-2 sm:top-4 top-2">
        <Button onClick={() => setShowInfo(true)} className="w-[170px] bg-cyan-700 hover:bg-cyan-600 text-white text-sm font-bold px-4 py-2 rounded">
          How it works
        </Button>
        <Button onClick={() => setShowRedemptionInfo(true)} className="w-[170px] bg-cyan-700 hover:bg-cyan-600 text-red-600 text-sm font-bold px-4 py-2 rounded animate-pulse shadow-lg">
          Redemption? <span className="ml-1">ℹ️</span>
        </Button>
        <Button onClick={() => setShowDareInfo(true)} className="w-[170px] bg-red-700 hover:bg-red-600 text-white text-sm font-bold px-4 py-2 rounded animate-pulse shadow-lg">
          Dare Wheel Info 🔥
        </Button>
        <BuyButton />
      </div>

    {isMobile && (
      <div className="relative z-30 mb-2">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="flex items-center gap-2 text-white bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-full text-sm shadow-lg"
        >
          <FiMenu />
          Menu
        </button>
        {mobileMenuOpen && (
          <div className="absolute mt-2 right-0 bg-gray-800 border border-gray-700 rounded-xl shadow-xl p-2 w-[200px] space-y-2">
            <Button
              onClick={() => {
                setShowInfo(true);
                setMobileMenuOpen(false);
              }}
              className="bg-cyan-700 hover:bg-cyan-600 w-full text-sm"
            >
              How it works
            </Button>
            <Button
              onClick={() => {
                setShowRedemptionInfo(true);
                setMobileMenuOpen(false);
              }}
              className="bg-yellow-700 hover:bg-yellow-600 w-full text-sm"
            >
              Redemption? ℹ️
            </Button>
            <Button
              onClick={() => {
                setShowDareInfo(true);
                setMobileMenuOpen(false);
              }}
              className="bg-red-700 hover:bg-red-600 w-full text-sm animate-pulse"
            >
              Dare Wheel Info 🔥
            </Button>
            <BuyButton />
          </div>
        )}
      </div>
    )}
      
  <h1 className="text-4xl sm:text-2xl font-extrabold mb-6 drop-shadow-lg text-red-600">
    🎰 Regret Roulette 🎰
  </h1>

  {doublePrizeActive && (
    <p className="text-yellow-400 font-bold text-sm mb-2">
      🎯 Your next prize will be doubled!
    </p>
  )}

  <CardContent className="w-full sm:max-w-5xl relative">
    {/* Desktop layout */}
    <div className="hidden sm:flex sm:justify-center w-full">
      {/* Midden: wiel + knop */}
      <div className="flex flex-col items-center">
        <RegretWheel spinning={spinning} targetIndex={resultIndex} onSpinComplete={handleEndSpin} />
        {isConnected ? (
          <Button
            onClick={handleSpin}
            disabled={spinning}
            className="mt-4 bg-cyan-700 hover:bg-cyan-600 text-white text-sm font-bold px-4 py-2 rounded"
          >
            {spinning ? "Spinning..." : "Spin the Wheel (100k $REGRET)"}
          </Button>
        ) : (
          <Button
            onClick={handleConnect}
            className="mt-4 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
          >
            Connect Wallet To Play
          </Button>
        )}
      </div>
    </div>

    {/* Last Spins rechtsonder op desktop */}
    <div className="hidden sm:block absolute left-0 bottom-0 mr-4 mb-4">
      <LiveFeed spins={liveSpins} />
    </div>

    {/* Mobiel layout */}
    <div className="block sm:hidden flex flex-col items-center">
      <RegretWheel spinning={spinning} targetIndex={resultIndex} onSpinComplete={handleEndSpin} />
      {isConnected ? (
        <Button
          onClick={handleSpin}
          disabled={spinning}
          className="mt-4 bg-cyan-700 hover:bg-cyan-600 text-white text-sm font-bold px-4 py-2 rounded"
        >
          {spinning ? "Spinning..." : "Spin the Wheel (100k $REGRET)"}
        </Button>
      ) : (
        <Button
          onClick={handleConnect}
          className="mt-4 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
        >
          Connect Wallet To Play
        </Button>
      )}
      <div className="mt-4 w-full">
        <LiveFeed spins={liveSpins} />
      </div>
    </div>
  </CardContent>

      {showPopup && result && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="rounded-2xl p-6 sm:p-8 w-[90vw] max-w-sm text-center text-white shadow-2xl transition-all duration-500 bg-gray-900 border-2 border-cyan-500">
            <h2 className="text-2xl font-bold mb-4 text-green-400">
              {result.label === "You Lose ❌" && "Do you already feel the regret?"}
              {result.label === "Tax the wallet 💸" && "Thanks for the tip, we'll take care of your money now."}
              {result.label === "Regret Mirror 🪞" && "Look at yourself… You just got a refund."}
              {result.label === "Free Spin 🎉" && "The wheel forgives. Spin again."}
              {result.label === "Double Prize 🔁" && "Double or nothing.     Don't mess it up."}
              {result.label === "Mystery 🪄" && `It's a mystery prize! You won:`}
              {["10k $REGRET", "50k $REGRET", "100k $REGRET", "250k $REGRET", "500k $REGRET", "JACKPOT 💎"].includes(result.label) && "You won:"}
            </h2>
            <p className="text-xl mb-2">
              {result.label === "Mystery 🪄" && mysteryAmount
                ? `${finalPayout?.toLocaleString()} $REGRET`
                : ["10k $REGRET", "50k $REGRET", "100k $REGRET", "250k $REGRET", "500k $REGRET", "Regret Mirror 🪞", "JACKPOT 💎"].includes(result.label)
                ? finalPayout?.toLocaleString() + " $REGRET"
                : result.label}
            </p>

           {showDareOption && (
    <div className="flex flex-col space-y-2 mt-4">
      <Button
        onClick={() => {
          setShowDareWheel(true);
          setShowPopup(false);
        }}
        className="bg-red-700 hover:bg-red-600 text-white font-bold py-2 px-4 rounded"
      >
        Dare to spin for bigger prizes 🔥
      </Button>
      <Button
        onClick={() => {
          setShowPopup(false);
          setLiveSpins(prev => [
            { wallet: address, result: result.label },
            ...prev.slice(0, 9),
          ]);
        }}
        className="bg-cyan-500 text-black font-bold px-4 py-2 rounded"
      >
        No thanks, I'll take this win
      </Button>
    </div>
  )}

  {!showDareOption && (
    <Button
      onClick={() => {
        setShowPopup(false);
        setLiveSpins(prev => [
          { wallet: address, result: result.label },
          ...prev.slice(0, 9),
        ]);
      }}
      className="mt-2 bg-cyan-500 text-black font-bold px-4 py-2 rounded"
    >
      OK
    </Button>
  )}

  <ShareButton label={result.label} amount={finalPayout} />
  </div>
        </div>
      )}             

      {showRedemptionSpin && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center">
          <div className="bg-gray-900 border-2 border-yellow-500 p-6 rounded-xl text-center max-w-sm">
            <h2 className="text-yellow-400 text-xl font-bold mb-2 animate-pulse">You lost twice in a row...</h2>
            <p className="text-white text-sm mb-4">Already regretting it? This one's on the house.</p>
            <RedemptionWheel
              onComplete={(segment) => {
                setRedemptionResult(segment);
                setShowRedemptionPopup(true);
                if (segment.value > 0) {
                  // Use the contract's spin method for redemption prizes
                  const rouletteContract = getContract({
                    address: regretRouletteAddress,
                    abi: regretRouletteABI,
                    client: walletClient
                  });

                  rouletteContract.write.spin({
                    gas: 500000n,
                    account: address
                  }).then(() => {
                    setShowConfetti(true);
                    setTimeout(() => setShowConfetti(false), 3000);
                  }).catch(err => {
                    console.error('Failed to distribute redemption prize:', err);
                    alert('Failed to distribute prize. Please try again.');
                  });
                }
                if (segment.label === "Free Spin 🎉") setFreeSpinActive(true);
                if (segment.label === "Refund 🪞") {
                  // Handle refund separately
                  const rouletteContract = getContract({
                    address: regretRouletteAddress,
                    abi: regretRouletteABI,
                    client: walletClient
                  });

                  rouletteContract.write.spin({
                    gas: 500000n,
                    account: address
                  }).then(() => {
                    setShowConfetti(true);
                    setTimeout(() => setShowConfetti(false), 3000);
                  }).catch(err => {
                    console.error('Failed to distribute refund:', err);
                    alert('Failed to distribute refund. Please try again.');
                  });
                }
              }}
            />
          </div>
        </div>
      )}

      {showRedemptionPopup && redemptionResult && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="rounded-2xl p-6 sm:p-8 w-[90vw] max-w-sm text-center text-white shadow-2xl transition-all duration-500 bg-gray-900 border-2 border-cyan-500">
            <h2 className="text-xl font-bold mb-4 text-red-400">Redemption Result</h2>
            <p className="text-xl font-bold mb-4">{redemptionResult.label}</p>
            <ShareButton label={`Redemption: ${redemptionResult.label}`} amount={redemptionResult.value} />
            <Button
              onClick={() => {
                setShowRedemptionPopup(false);
                setShowRedemptionSpin(false);
              }}
              className="mt-4 bg-cyan-500 text-black font-bold px-4 py-2 rounded"
            >
              Close
            </Button>
          </div>
        </div>
      )}

  {showDareWheel && (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center">
      <DareWheel
        prizeWon={finalPayout}
        onComplete={(segment) => {
          setDareResult(segment);
          setShowDareWheel(false);
          setShowDarePopup(true);

          if (segment.payout > 0) {
            // Instead of using sendPrizeToWallet, we'll use the contract's spin method
            // with a special prize type for dare wheel winnings
            const rouletteContract = getContract({
              address: regretRouletteAddress,
              abi: regretRouletteABI,
              client: walletClient
            });

            rouletteContract.write.spin({
              gas: 500000n,
              account: address
            }).then(() => {
              setShowConfetti(true);
              setTimeout(() => setShowConfetti(false), 3000);
            }).catch(err => {
              console.error('Failed to distribute dare wheel prize:', err);
              alert('Failed to distribute prize. Please try again.');
            });
          }

          // Update feed with Dare Result
          setLiveSpins((prev) => [
            { wallet: address, result: segment.label },
            ...prev.slice(0, 9),
          ]);
        }}
      />
    </div>
  )}

  {showDarePopup && dareResult && (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-80">
      <div className="rounded-xl p-6 sm:p-8 w-[90vw] max-w-sm text-center bg-gray-900 border-2 border-red-600 text-white shadow-2xl">
        <h2 className="text-xl font-bold mb-4 text-red-400">🔥 Dare Wheel Result</h2>
        <p className="text-xl font-bold mb-4">{dareResult.label}</p>
        <p className="text-md text-white mb-4">{dareResult.payout.toLocaleString()} $REGRET</p>
        <Button
          onClick={() => {
            setShowDarePopup(false);
            setFinalPayout(null);
          }}
          className="mt-2 bg-cyan-500 text-black font-bold px-4 py-2 rounded"
        >
          Close
        </Button>
      </div>
    </div>
  )}

      {showWarning && <WarningModal onClose={() => setShowWarning(false)} />}
      <HowItWorksModal open={showInfo} onClose={() => setShowInfo(false)} />
      <RedemptionInfoModal open={showRedemptionInfo} onClose={() => setShowRedemptionInfo(false)} />
      <DareInfoModal open={showDareInfo} onClose={() => setShowDareInfo(false)} />
    </div>
  </>
  );
}